/* eslint-disable import/prefer-default-export */
import { ComplianceType, Compliance } from '@centecomm/portal-service';
import { useCompliance } from '@centecomm/portal-service-react';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { WelcomeActionTypes, WelcomeActions } from '../actions';
import { complianceForOccupant } from '../selectors';

type WelcomeStateComplianceHook = (occupantId: number) => {
    isLoaded: boolean;
    hasError: boolean;
    compliance: Compliance[] | null;
    error: string | undefined;
    complianceByType: (complianceType: ComplianceType) => Compliance | null;
};

export const useComplianceFromWelcomeState: WelcomeStateComplianceHook = (occupantId) => {
    const dispatch = useDispatch();

    const compliance: Compliance[] | null = useSelector(complianceForOccupant(occupantId));

    const [errorState, setErrorState] = React.useState<string | undefined>();
    const [loadedOccupantId, setLoadedOccupantId] = React.useState<number | string>(0);
    const [pending, togglePending] = React.useState<boolean>(false);

    const { getCompliance } = useCompliance();

    React.useEffect(() => {
        if (!pending && !compliance && occupantId !== loadedOccupantId) {
            togglePending(true);

            getCompliance(occupantId)
                .then((complianceResponse) => {
                    setLoadedOccupantId(occupantId);

                    dispatch({
                        type: WelcomeActions.ADD_OCCUPANT_COMPLIANCE,
                        payload: {
                            occupantId,
                            compliance: complianceResponse,
                        },
                    } as WelcomeActionTypes);

                    togglePending(false);
                })
                .catch(() => {
                    setErrorState('We were not able to access compliance data.');

                    togglePending(false);
                });
        }
    }, [occupantId, compliance]);

    const complianceByType = (type: ComplianceType) =>
        !!compliance && !!compliance.length
            ? compliance.find(({ complianceType }: Compliance) => complianceType === type) || null
            : null;

    return {
        isLoaded: !!compliance,
        hasError: !!errorState,
        compliance,
        error: errorState,
        complianceByType,
    };
};
